import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { chatHistory } from "../api/ChatBotApiCall";
import { ChatConversation } from "../api/responsePayload/ChatResponse";
import { showConsoleError } from "../util/ConsoleMessage";
import { setPagination } from "./pagination.slice";

interface ChatState {
  chatHistory: ChatConversation[];
  loading: boolean;
  error?: string;
}

const initialChatState: ChatState = { chatHistory: [], loading: false };

export const fetchChatHistory = createAsyncThunk(
  "chat/fetchHistory",
  async (
    args:
      | { endpoint?: string; queryParams?: Record<string, any>; page?: number }
      | undefined,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const res = await chatHistory(
        args?.endpoint,
        {},
        args?.queryParams ?? {},
        args?.page
      );
      if (res) {
        dispatch(
          setPagination({
            currentPage: res.currentPage,
            nextPageUrl: res.next,
            prevPageUrl: res.previous,
            totalPages: res.totalPages,
            totalCount: res.count,
          })
        );
        return res.results as ChatConversation[];
      }
      return [] as ChatConversation[];
    } catch (e: any) {
      showConsoleError("fetchChatHistory error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const chatSlice = createSlice({
  name: "chat",
  initialState: initialChatState,
  reducers: {
    setChatActive(
      state,
      action: PayloadAction<{
        conversationId: string | number;
        clickedId: string | number;
      }>
    ) {
      const { conversationId, clickedId } = action.payload;
      const conv = state.chatHistory.find(
        (c) => String(c.conversationId) === String(conversationId)
      );
      if (!conv) return;

      const clicked = conv.messages.find(
        (m) => String(m.id) === String(clickedId)
      );
      const threadRootId = clicked?.inReplyTo ?? clickedId;

      conv.messages.forEach((m) => {
        const belongsToThread =
          String(m.id) === String(clickedId) ||
          String(m.id) === String(threadRootId) ||
          String(m.inReplyTo ?? "") === String(threadRootId);
        m.isActive = belongsToThread;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.chatHistory = action.payload;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setChatActive } = chatSlice.actions;
export default chatSlice.reducer;
